import { BookOpen, Copy, Download, FileClock, Pencil, Printer, Share2, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type * as React from 'react';

import { type EventActionIconId } from '../../lib/access-legitimacy-resolvers';
import { cn } from '../../lib/utils';
import { UserTypeBadge } from '../UserTypeBadge';

/**
 * One row in the action-history mini-table. `actionIconId` is the closed union
 * shared with `resolveActionIcon` — every action in the server vocabulary has a
 * distinct glyph; a label outside that set resolves to `'other'`, rendering the
 * generic fallback glyph (design v6 §3.3).
 */
export interface EventActionHistoryRecord {
  /** Unique id (stable per event-action pair). Drives React key. */
  id: string;
  /** ISO date string for the action timestamp. */
  dateTime: string;
  /** Drives `UserTypeBadge` glyph. */
  userType: 'human' | 'serviceAccount';
  /** External-human marker (drives `UserTypeBadge` privileged overlay). */
  isExternal: boolean;
  /** Display string for the actor (email, opaque id, etc.). */
  actor: string;
  /** Human-readable action label rendered in the table cell ("View", "Edit", …). */
  action: string;
  actionIconId: EventActionIconId;
}

/**
 * Closed-union map from `actionIconId` → lucide glyph component. Every action in
 * the server vocabulary has a distinct glyph; `'other'` is the defensive
 * fallback (a neutral file-activity glyph) for any label outside that set, so an
 * unknown action takes a glyph rather than being dropped.
 */
const ACTION_ICON_GLYPHS = {
  read: BookOpen,
  download: Download,
  edit: Pencil,
  share: Share2,
  delete: Trash2,
  print: Printer,
  copy: Copy,
  other: FileClock,
} as const satisfies Readonly<Record<EventActionIconId, LucideIcon>>;

export interface EventActionHistoryColumnLabels {
  dateTime: string;
  userType: string;
  actor: string;
  action: string;
}

export interface EventActionHistoryTableProps {
  records: readonly EventActionHistoryRecord[];
  columns: EventActionHistoryColumnLabels;
  formatDateTime: (iso: string) => string;
  className?: string;
}

/**
 * `EventActionHistoryTable` — 4-column mini-table (date/time, user type, actor,
 * action) for the action history of a single event (Figma `5:10179`). Each
 * row's action cell renders a glyph from `ACTION_ICON_GLYPHS` next to the
 * label. Pure presentational.
 */
export function EventActionHistoryTable({
  records,
  columns,
  formatDateTime,
  className,
}: EventActionHistoryTableProps): React.ReactElement {
  return (
    <table className={cn('w-full border-collapse', className)}>
      <thead>
        <tr className="border-b border-grey-200">
          <th
            scope="col"
            className="w-[110px] py-1 text-left font-outfit text-[10px] font-medium leading-[1.2] text-dawn-black"
          >
            {columns.dateTime}
          </th>
          <th
            scope="col"
            className="w-[112px] py-1 text-left font-outfit text-[10px] font-medium leading-[1.2] text-dawn-black"
          >
            {columns.userType}
          </th>
          <th
            scope="col"
            className="w-[128px] py-1 text-left font-outfit text-[10px] font-medium leading-[1.2] text-dawn-black"
          >
            {columns.actor}
          </th>
          <th
            scope="col"
            className="w-[128px] py-1 text-left font-outfit text-[10px] font-medium leading-[1.2] text-dawn-black"
          >
            {columns.action}
          </th>
        </tr>
      </thead>
      <tbody>
        {records.map((record) => {
          const ActionGlyph = ACTION_ICON_GLYPHS[record.actionIconId];
          return (
            <tr key={record.id} className="h-7 align-middle">
              <td className="font-outfit text-[10px] leading-[1.2] text-dawn-black">
                {formatDateTime(record.dateTime)}
              </td>
              <td>
                <UserTypeBadge
                  type={record.userType === 'human' ? 'HI' : 'NHI'}
                  isPrivileged={record.isExternal}
                  size="sm"
                />
              </td>
              <td className="font-outfit text-[10px] leading-[1.2] text-dawn-black">
                {record.actor}
              </td>
              <td>
                <span className="inline-flex items-center gap-1 font-outfit text-[10px] leading-[1.2] text-dawn-black">
                  <ActionGlyph className="h-3 w-3" aria-hidden="true" />
                  {record.action}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

EventActionHistoryTable.displayName = 'EventActionHistoryTable';
